import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import {
  ICoverCandidateResponse,
  IReassignSourceImpactResponse,
  ICoverageCellDetailResponse,
  ICoverageMatrixResponse,
  ICoverageModelResponse,
  ICoverageWorkScheduleResponse,
  ISaveCoverageModelBody,
} from 'types/api/coverageApi.interface'
import { ICoverageSlice } from 'types/coverage.interface'
import { baseUrl } from 'utils/axios'
import { handleErrors } from 'utils/helpers'
import {
  buildDemoCell,
  buildDemoMatrix,
  demoApplyCover,
  demoApplyReassign,
  demoReassignPreview,
  demoConfirmSlot,
  demoCoverCandidates,
  demoModelsInForce,
  demoModelVersions,
  demoReminderCount,
  demoRevokeConfirm,
  saveDemoModels,
  syncCoverageRegistries,
  USE_COVERAGE_MOCKS,
} from 'pages/Shifts/Coverage/coverageDemoStore'

const syncRegistry = (thunkAPI: any) => syncCoverageRegistries(thunkAPI.getState())

const initialState: ICoverageSlice = {
  isLoading: false,
  hasError: false,
  matrix: null,
  cell: null,
  models: [],
  versions: [],
  candidates: [],
  candidatesLoading: false,
}

export const getCoverageMatrix = createAsyncThunk(
  'coverage/getMatrix',
  async (params: any, thunkAPI) => {
    if (USE_COVERAGE_MOCKS) {
      syncRegistry(thunkAPI)
      return buildDemoMatrix(params.siteNodeId || 1, params.date)
    }
    try {
      const resp: AxiosResponse<ICoverageMatrixResponse> = await baseUrl.get('v1/coverage/matrix', {
        params,
      })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getCoverageCell = createAsyncThunk(
  'coverage/getCell',
  async (params: any, thunkAPI) => {
    if (USE_COVERAGE_MOCKS) {
      syncRegistry(thunkAPI)
      return buildDemoCell(params.siteNodeId || 1, params.departmentId, params.shiftId, params.date)
    }
    try {
      const resp: AxiosResponse<ICoverageCellDetailResponse> = await baseUrl.get('v1/coverage/cell', {
        params,
      })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getCoverageModels = createAsyncThunk(
  'coverage/getModels',
  async (params: any, thunkAPI) => {
    if (USE_COVERAGE_MOCKS) {
      syncRegistry(thunkAPI)
      return demoModelsInForce(params.departmentId, params.siteNodeId || 1, params.onDate)
    }
    try {
      const resp: AxiosResponse<ICoverageModelResponse[]> = await baseUrl.get('v1/coverage/model', {
        params,
      })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getCoverageModelVersions = createAsyncThunk(
  'coverage/getModelVersions',
  async (params: any, thunkAPI) => {
    if (USE_COVERAGE_MOCKS) {
      syncRegistry(thunkAPI)
      return demoModelVersions(params.departmentId, params.siteNodeId || 1)
    }
    try {
      const resp: AxiosResponse<ICoverageModelResponse[]> = await baseUrl.get(
        'v1/coverage/model/version',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const saveCoverageModels = createAsyncThunk(
  'coverage/saveModels',
  async (body: ISaveCoverageModelBody, thunkAPI) => {
    if (USE_COVERAGE_MOCKS) {
      syncRegistry(thunkAPI)
      return saveDemoModels(body)
    }
    try {
      const resp: AxiosResponse<ICoverageModelResponse[]> = await baseUrl.put('v1/coverage/model', body)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const sendCoverageReminder = createAsyncThunk(
  'coverage/sendReminder',
  async (body: any, thunkAPI) => {
    if (USE_COVERAGE_MOCKS) {
      return { remindedUserCount: demoReminderCount(body.siteNodeId, body.departmentId, body.shiftId) }
    }
    try {
      const resp: AxiosResponse<{ remindedUserCount: number }> = await baseUrl.post(
        'v1/coverage/roster/reminder',
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getCoverCandidates = createAsyncThunk(
  'coverage/getCoverCandidates',
  async (params: any, thunkAPI) => {
    if (USE_COVERAGE_MOCKS) {
      return demoCoverCandidates(params.siteNodeId || 1, params.departmentId)
    }
    try {
      const resp: AxiosResponse<ICoverCandidateResponse[]> = await baseUrl.get(
        'v1/coverage/cover/candidate',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const applyCoverageCover = createAsyncThunk(
  'coverage/applyCover',
  async (input: any, thunkAPI) => {
    if (USE_COVERAGE_MOCKS) {
      demoApplyCover(input.userId, input.shiftId, input.siteNodeId, input.departmentId)
      return { ok: true }
    }
    try {
      const resp: AxiosResponse<ICoverageWorkScheduleResponse> = await baseUrl.post('v1/coverage/cover', {
        userId: input.userId,
        shiftId: input.shiftId,
        date: input.date,
      })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const confirmCoverageSlot = createAsyncThunk(
  'coverage/confirmSlot',
  async (workScheduleId: number, thunkAPI) => {
    if (USE_COVERAGE_MOCKS) {
      demoConfirmSlot(workScheduleId)
      return { ok: true }
    }
    try {
      const resp: AxiosResponse<ICoverageWorkScheduleResponse> = await baseUrl.post(
        `v1/coverage/roster/${workScheduleId}/confirm`,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const revokeCoverageConfirm = createAsyncThunk(
  'coverage/revokeConfirm',
  async (workScheduleId: number, thunkAPI) => {
    if (USE_COVERAGE_MOCKS) {
      demoRevokeConfirm(workScheduleId)
      return { ok: true }
    }
    try {
      const resp: AxiosResponse<ICoverageWorkScheduleResponse> = await baseUrl.post(
        `v1/coverage/roster/${workScheduleId}/confirm/revoke`,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getReassignPreview = createAsyncThunk(
  'coverage/reassignPreview',
  async (input: any, thunkAPI) => {
    if (USE_COVERAGE_MOCKS) return demoReassignPreview(input.workScheduleId, input.targetShiftId)
    try {
      const resp: AxiosResponse<IReassignSourceImpactResponse> = await baseUrl.get(
        `v1/coverage/roster/${input.workScheduleId}/reassign/preview`,
        { params: { targetShiftId: input.targetShiftId } },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const applyCoverageReassign = createAsyncThunk(
  'coverage/applyReassign',
  async (input: any, thunkAPI) => {
    if (USE_COVERAGE_MOCKS) {
      demoApplyReassign(input.workScheduleId, input.targetShiftId)
      return { ok: true }
    }
    try {
      const resp: AxiosResponse<ICoverageWorkScheduleResponse> = await baseUrl.post(
        `v1/coverage/roster/${input.workScheduleId}/reassign`,
        { targetShiftId: input.targetShiftId, acknowledgeSourceImpact: input.acknowledgeSourceImpact },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const coverageSlice = createSlice({
  name: 'coverage',
  initialState,
  reducers: {
    clearCoverageCell: (state) => {
      state.cell = null
      state.candidates = []
    },
  },
  extraReducers: {
    [getCoverageMatrix.pending.type]: (state) => {
      state.isLoading = true
      state.hasError = false
    },
    [getCoverageMatrix.fulfilled.type]: (
      state: ICoverageSlice,
      { payload }: { payload: ICoverageMatrixResponse },
    ) => {
      state.isLoading = false
      state.matrix = payload
    },
    [getCoverageMatrix.rejected.type]: (state, action) => {
      state.isLoading = false
      state.hasError = true
      handleErrors(action.payload)
    },
    [getCoverageCell.fulfilled.type]: (
      state: ICoverageSlice,
      { payload }: { payload: ICoverageCellDetailResponse | null },
    ) => {
      state.cell = payload
    },
    [getCoverageCell.rejected.type]: (state, action) => {
      handleErrors(action.payload)
    },
    [getCoverageModels.fulfilled.type]: (
      state: ICoverageSlice,
      { payload }: { payload: ICoverageModelResponse[] },
    ) => {
      state.models = payload
    },
    [getCoverageModels.rejected.type]: (state, action) => {
      handleErrors(action.payload)
    },
    [getCoverageModelVersions.fulfilled.type]: (
      state: ICoverageSlice,
      { payload }: { payload: ICoverageModelResponse[] },
    ) => {
      state.versions = payload
    },
    [getCoverageModelVersions.rejected.type]: (state, action) => {
      handleErrors(action.payload)
    },
    [saveCoverageModels.fulfilled.type]: (
      state,
      { payload }: { payload: ICoverageModelResponse[] },
    ) => {
      toast.success(
        payload.length
          ? `Coverage model saved (${payload.length} shift${payload.length === 1 ? '' : 's'} updated)`
          : 'No changes to save',
      )
      payload.forEach((m) => {
        if (m.staffingWarning) toast.warn(m.staffingWarning)
      })
    },
    [saveCoverageModels.rejected.type]: (state, action) => {
      handleErrors(action.payload)
    },
    [sendCoverageReminder.fulfilled.type]: (
      state,
      { payload }: { payload: { remindedUserCount: number } },
    ) => {
      toast.success(
        payload.remindedUserCount
          ? `Reminder sent to ${payload.remindedUserCount} ${payload.remindedUserCount === 1 ? 'person' : 'people'}`
          : 'Everyone on this cell has already answered',
      )
    },
    [sendCoverageReminder.rejected.type]: (state, action) => {
      handleErrors(action.payload)
    },
    [getCoverCandidates.pending.type]: (state) => {
      state.candidatesLoading = true
      state.candidates = []
    },
    [getCoverCandidates.fulfilled.type]: (
      state: ICoverageSlice,
      { payload }: { payload: ICoverCandidateResponse[] },
    ) => {
      state.candidatesLoading = false
      state.candidates = payload
    },
    [getCoverCandidates.rejected.type]: (state, action) => {
      state.candidatesLoading = false
      handleErrors(action.payload)
    },
    [applyCoverageCover.fulfilled.type]: () => {
      toast.success('Cover added to the shift')
    },
    [applyCoverageCover.rejected.type]: (state, action) => {
      handleErrors(action.payload)
    },
    [confirmCoverageSlot.rejected.type]: (state, action) => {
      handleErrors(action.payload)
    },
    [getReassignPreview.rejected.type]: (state, action) => {
      handleErrors(action.payload)
    },
    [applyCoverageReassign.fulfilled.type]: () => {
      toast.success('Person moved — they must confirm the new shift')
    },
    [applyCoverageReassign.rejected.type]: (state, action) => {
      handleErrors(action.payload)
    },
    [revokeCoverageConfirm.rejected.type]: (state, action) => {
      handleErrors(action.payload)
    },
  },
})

export const { clearCoverageCell } = coverageSlice.actions
export default coverageSlice.reducer
